/**
 * 中文语言包
 *
 * Structure must match en.ts — the type check below will
 * fail at compile-time if any key is missing or misnamed.
 */
import type en from './en';

type DeepRecord<T> = { [K in keyof T]: T[K] extends Record<string, unknown> ? DeepRecord<T[K]> : string };
type Messages = DeepRecord<typeof en>;

const zh: Messages = {
  // 通用 / 共享
  common: {
    save: '保存',
    saving: '保存中...',
    saved: '✓ 已保存',
    cancel: '取消',
    confirm: '确认',
    delete: '删除',
    deleting: '删除中...',
    edit: '编辑',
    add: '添加',
    remove: '移除',
    close: '关闭',
    copy: '复制',
    search: '搜索...',
    loading: '加载中...',
    noData: '暂无数据',
    send: '发送',
    stop: '⏹ 停止',
    restore: '恢复',
    archive: '归档',
    new: '新增',
    all: '全部',
    back: '返回',
    update: '更新',
    create: '创建',
    done: '已完成',
    failed: '失败',
    active: '活跃',
    inactive: '停用',
    online: '在线',
    offline: '离线',
    pending: '待处理',
    running: '运行中...',
    complete: '完成',
    completed: '已完成',
    cancelled: '已取消',
    accepted: '已采纳',
    rejected: '已拒绝',
    published: '已发布',
    draft: '草稿',
    archived: '已归档',
    deleted: '删除',
    updated: '更新',
    important: '重要',
    urgent: '紧急',
    overview: '概览',
    noMatchingRows: '无匹配行',
    noRecordsFound: '未找到记录',
    saveFailed: '保存失败',
    deleteFailed: '删除失败',
    loadFailed: '加载失败',
    openInNewTab: '在新标签页打开',
    tryDifferentSearch: '请尝试其他搜索词或分类',
    tryAdjustFilters: '请尝试调整筛选条件',
    star: '收藏',
    unstar: '取消收藏',
    untitled: '未命名',
    collapse: '收起',
    expand: '展开',
    empty: '（空）',
    notAnswered: '（未回答）',
    applyingChanges: '应用中...',
    comment: '备注',
    networkError: '网络错误',
    createFailed: '创建失败',
    operationFailed: '操作失败',
    neverLoggedIn: '从未登录',
    status: '状态',
    priority: '优先级',
    assignee: '负责人',
    unassigned: '未分配',
    self: '自己',
    startDate: '开始日期',
    endDate: '结束日期',
    dueDate: '截止日期',
    description: '描述',
    name: '名称',
    nickname: '昵称',
    email: '邮箱',
    role: '角色',
    color: '颜色',
    tags: '标签',
    refresh: '刷新',
    moreActions: '更多操作',
    viewDetails: '查看详情',
    noRecords: '暂无记录',
    you: '你',
    select: '选择',
    selectProject: '选择项目',
    selected: '已选 {count} 个',
    batchUpdateStatus: '批量修改状态：',
    assign: '指派：',
    moveTo: '移动到：',
    cancelSelection: '✕ 取消',
    searchTasks: '搜索任务...',
    total: '共 {count} 个',
  },

  // 应用外壳 / 导航
  app: {
    chat: '对话',
    projects: '项目',
    settings: '设置',
    noPermission: '您没有访问此页面的权限。',
    myProfile: '我的账号',
    preferences: '偏好设置',
    prompts: '快捷指令',
    knowledge: '知识库',
    logout: '退出登录',
    logoutTitle: '退出登录？',
    logoutDesc: '你将退出当前账号，需要重新登录后继续使用。',
    agentPanel: 'Global Agent (⌘K)',
  },

  // 登录（产品名来自 branding seam，不走 i18n）
  login: {
    subtitle: '登录以继续',
    teamLogin: '团队登录',
    guestAccess: '访客访问',
    emailPlaceholder: '邮箱',
    password: '密码',
    accessCode: '访问码',
    signIn: '登录',
    enter: '进入',
    signingIn: '登录中...',
    emailRequired: '请输入邮箱',
    loginFailed: '登录失败',
    invalidPassword: '密码错误',
  },

  // 偏好设置
  preferences: {
    title: '偏好设置',
    theme: '主题',
    themeLight: '浅色',
    themeDark: '深色',
    themeSystem: '跟随系统',
    language: '语言',
    personalNotes: '个人备注',
    notesHint: '您的备注将注入到 AI Agent 的上下文中，用于个性化回复。例如：您的角色、语言偏好、沟通风格或关注领域。',
    notesPlaceholder: '例如：\n我是产品经理。\n请用中文回复。\n回答尽量简洁、偏技术向。',
    charactersRemaining: '剩余 {count} 个字符',
  },

  // 品牌工作台（仅超级管理员）
  brandingStudio: {
    intro:
      '定制这个工作区的外观——先选一个起始风格，再微调颜色、字体和圆角，右侧实时预览。调满意后保存到工作区（连同名称、Logo 与团队 Sprouty），所有成员从登录页起即可看到；也可以导出 CSS 给 Fork 使用。',

    // 起始风格
    presets: '起始风格',
    preset_greenhouse: 'Greenhouse',
    preset_slate: '石板蓝',
    preset_sunset: '日落橙',
    preset_editorial: '杂志感',
    preset_graphite: '石墨黑',

    // 分区
    brandColor: '品牌色',
    semantic: '状态色',
    tokens: '表面、文字与边框',
    typography: '排版',
    shape: '圆角',
    groupSurface: '表面',
    groupText: '文字',
    groupBorder: '边框',

    // 控件
    fontDefault: '系统默认',
    fontCustomPlaceholder: "或输入字体栈——'Brand Sans', system-ui, sans-serif",
    fontMonoPlaceholder: "等宽字体——'JetBrains Mono', ui-monospace, monospace",
    textScale: '字号',
    radius: '圆润度',
    preview: '实时预览',

    // 提示——说明每个控件会改变什么，用大白话
    tipPresets: '一键套用一整套观感——品牌色、字体和圆角一起变。微调前先快速感受不同风格。',
    tipBrand: '主强调色：主按钮、链接、选中项、聚焦描边和全站高亮都会跟着变。',
    tipSemantic:
      '反馈色——成功 / 警告 / 危险 / 信息 状态在标签、徽章、提示条和状态点上的配色。中性色跟随文字/边框色，主色跟随品牌色。',
    tipTokens: '背景层次（页面 → 卡片）、正文文字的深浅、以及分隔线和描边。',
    tipTypography: '界面字体和整体字号，从菜单到会话回复处处生效。',
    tipShape: '圆角的程度——按钮、输入框、卡片、标签和弹窗都会跟随。',
    tipPreview: '真实组件，调整即时呈现。在浅色/深色间切换可分别检查——两套颜色改动各自保留。',
    tipExport: '把你改动过的样式复制成一段代码，粘到工作区即可长期生效。重置会清空预览。',

    // 导出
    exportTitle: '保存这套样式',
    copy: '复制样式',
    copied: '已复制',
    reset: '重置',
    noChanges: '暂无改动——选一个起始风格或调整一个颜色即可生成导出内容。',

    // 预览画布——分区标题
    pvTypography: '文字',
    pvControls: '按钮与输入',
    pvSemantic: '状态色',
    pvChat: '会话',

    // 预览画布——贴近 Greenhouse 语气的示例内容
    sampleHeading: '你的工作区，你的风格',
    sampleBody: 'Greenhouse 为每个团队配一个横跨对话、知识库和项目的 AI 助手。这就是你邀请的成员看到的正文观感。',
    sampleMeta: '刚刚更新 · 来自助手',
    samplePrimaryBtn: '新对话',
    sampleInput: '有什么想问助手的…',
    sampleToggle: '与团队共享',
    sampleOnline: '已连接',
    sampleRunning: '运行中',
    sampleFailed: '需要处理',
    sampleUserMsg: '帮我汇总上季度，并按产品线分组。',
    sampleAssistantMsg: '上季度整体营收增长，亮点如下：',
    sampleBullet1: '增长最快，主要来自企业客户续约',
    sampleBullet2: '新注册带动大幅上升——见',
    sampleLink: '用量报告',
    sampleModelMeta: '3.8 秒内回答',

    // 工作区持久化（branding.* 工作区配置）
    workspaceTitle: '工作区标识',
    tipWorkspace:
      '保存到工作区后对所有成员生效（从登录页开始）——租户名、Logo、这些样式与团队 Sprouty。无需改代码、无需重启。',
    productName: '产品名称',
    logo: 'Logo',
    logoUpload: '上传',
    logoRemove: '移除 Logo',
    logoHint: '支持 PNG / JPEG / SVG / WebP，最大 256 KB。显示在侧边栏和登录页。',
    logoBadType: '不支持的图片格式',
    logoTooLarge: '图片超过 256 KB',
    teamSprouty: '团队 Sprouty',
    teamSproutyOn: '自定义',
    teamSproutyOff: '恢复默认',
    teamSproutyHint: '内置 Agent 的默认形象。自定义它，让工作区拥有自己的吉祥物。',
    saveWorkspace: '保存到工作区',
    clearWorkspace: '重置工作区品牌',
    workspaceSaved: '工作区品牌已保存',
    workspaceCleared: '工作区品牌已重置',
  },

  // 共享 Sprouty 形象编辑器（Profile 编辑器 + 品牌工作室）
  sproutyDesigner: {
    color: '颜色',
    customColor: '自定义',
    bodyColor: '身体',
    leafColor: '叶子',
    faceStyle: '表情',
    accessories: '配件',
    slot_hat: '帽子',
    slot_glasses: '眼镜',
    slot_held: '手持',
    none: '无',
    leafStyle: '叶型',
  },

  // 运行时配置（管理员）——基于注册表的工作区设置
  runtimeConfig: {
    intro:
      '在管理后台配置运行时凭据——模型端点、视觉、图像生成与联网搜索。此处保存的值优先于环境变量并立即生效；清空字段则回落到环境变量。',
    group_llm: '主模型',
    group_vision: '视觉',
    group_image_gen: '图像生成',
    group_search: '联网搜索',
    sourceDb: '工作区',
    sourceEnv: '环境变量',
    sourceNone: '未配置',
    envFallback: '环境变量回落：',
    secretPlaceholder: '输入新值以替换',
    secretSet: '已配置',
    clear: '清空',
    save: '保存更改',
    saved: '运行时配置已保存',
  },

  // 聊天
  chat: {
    newConversation: '新对话',
    history: '历史记录',
    new: '+ 新建',
    startConversation: '开始对话',
    defaultDescription: '提出问题或搜索知识库',
    tryAsking: '试试这样问',
    askPlaceholder: '输入问题...',
    askPlaceholderFull: '输入问题...（@ 提及，/ 快捷指令）',
    useSkillsDirective: '请使用以下技能完成本次任务：{skills}。',
    searchConversations: '搜索对话...',
    noConversationsFound: '未找到对话',
    sessionHistory: '会话历史',
    loadingHistory: '加载历史记录中...',
    sessionMovedToTrash: '会话已移至回收站',
    sessionPermanentlyDeleted: '会话已永久删除',
    deleteSessionConfirm: '永久删除此会话？此操作无法撤销。',
    deleteSessionTitle: '永久删除此会话？',
    deleteCannotUndo: '此操作不可撤销。',
    fastThinking: '快速 — 响应更快，推理较浅',
    deepThinking: '深度 — 响应较慢，推理更深入',
    jumpToLatest: '回到最新',
    newContent: '有新内容',
    streaming: '流式生成中...',
    howCanIHelp: '有什么可以帮助您的？',
    giveOverview: '介绍一下你能帮我做什么。',
    hereAreAnswers: '以下是我对您问题的回答：',
    translateToEnglish: '翻译为英文',
    pleaseTranslate: '请将以上内容翻译为英文...',
    editMessage: '编辑消息',
    regenerate: '重新生成',
    regenerateResponse: '重新生成回复',
    regenerateConfirm: '确定要重新生成此回复？',
    regenerateWarning: '当前回复将被新回复替换。',
    newResponse: '新回复',
    goodResponse: '满意回复',
    badResponse: '不满意回复',
    rateAndComment: '评分与备注',
    yourNote: '您的备注...',
    yourNoteOptional: '您的备注（可选）...',
    writeComment: '写评论...',
    addAnnotation: '添加批注',
    addNote: '添加备注',
    editNote: '编辑备注',
    clearAnnotations: '清除全部批注',
    quoteAndNote: '引用并添加批注',
    clearRatingFilter: '清除评分筛选',
    clickToEditName: '点击编辑名称',
    analyzedImage: '已分析图片',
    queuing: '排队中…',
  },

  // Agent 面板
  agent: {
    globalAssistant: '全局助手',
    noTasksYet: '暂无任务',
  },

  // 历史记录
  history: {
    title: '历史记录',
    searchPlaceholder: '搜索对话...',
    viewAll: '查看全部',
  },

  // 项目
  projects: {
    title: '项目',
    tasks: '任务',
    list: '列表',
    board: '看板',
    gantt: '甘特图',
    dragToCreate: '拖拽创建新任务',
    deleteTaskConfirm: '确定删除此任务？',
    deleteTaskDescription: '子任务也会被删除，此操作不可撤销。',
    taskDeleted: '任务已删除',
    todo: '待办',
    inProgress: '进行中',
    inReview: '评审中',
    onHold: '暂停',
    planning: '计划中',
    cards: '卡片',
    globalGantt: '时间线',
    allProjects: '所有项目',
    expandAll: '展开全部',
    collapseAll: '折叠全部',
    noTasks: '暂无任务',
    projectProgress: '进度',
    createProject: '创建项目',
    editProject: '编辑项目',
    projectName: '项目名称',
    projectNameRequired: '请输入项目名称',
    projectNamePlaceholder: '项目名称 *',
    projectDescPlaceholder: '项目描述（可选）',
    projectColor: '项目颜色',
    creating: '创建中...',
    milestone: '里程碑',
    addTask: '添加任务',
    changelog: '变更记录',
    statusUpdated: '状态已更新',
    taskStatusUpdated: '状态已更新',
    movedToProject: '已移动 {count} 个任务到「{target}」',
    tasksUpdated: '已更新 {count} 个任务',
    taskMovedToProject: '任务已移动到 {target}',
    members: '成员',
    member: '成员',
    owner: '负责人',
    selectMember: '选择用户...',
    memberAdded: '成员已添加',
    memberRemoved: '成员已移除',
    roleUpdated: '角色已更新',
    removeMember: '移除成员',
    removeMemberConfirm: '确认移除成员？',
    removeMemberConfirmDesc: '确认移除此成员：',
    setOwner: '设为负责人',
    setMember: '设为普通成员',
    visibility: '可见性',
    visibilityPublic: '公开',
    visibilityPrivate: '私有',
    visibilityPublicDesc: '团队所有成员可见',
    visibilityPrivateDesc: '仅项目成员可见',
  },

  // 任务管理
  task: {
    createTask: '创建任务',
    createSubtask: '创建子任务',
    createMilestone: '创建里程碑',
    taskTitle: '任务标题',
    taskTitleRequired: '请输入任务标题',
    milestoneNameRequired: '请输入里程碑名称',
    titlePlaceholder: '任务标题 *',
    milestonePlaceholder: '里程碑名称 *',
    descPlaceholder: '描述（可选）',
    milestoneHint: '里程碑用于标记项目中的关键节点，如发布日、评审日等',
    milestoneDate: '里程碑日期',
    estimatedHours: '预估工时(h)',
    tagsComma: '标签(逗号分隔)',
    parentTask: '父任务',
    subtasks: '子任务',
    comments: '评论',
    dependencies: '依赖关系',
    editDeps: '编辑',
    doneDeps: '完成',
    blockers: '前置任务（阻塞此任务）',
    dependents: '后续任务（被此任务阻塞）',
    searchTasksToAdd: '搜索任务添加前置...',
    noMatchingTasks: '无匹配任务',
    noDependencies: '暂无依赖关系',
    deleteConfirm: '确定删除此任务？',
    deleteConfirmDesc: '子任务也会被删除，此操作不可撤销。',
    deleted: '任务已删除',
    setMilestone: '设为里程碑',
    unsetMilestone: '取消里程碑',
    addSubtask: '添加子任务',
    reparent: '变为子任务',
    removeParent: '取消父任务',
    moveToProject: '移动到项目',
    setBlocker: '设置前置任务',
    deleteTask: '删除任务',
    editTask: '编辑任务',
    taskTitleLabel: '任务标题',
    descriptionMarkdown: '描述（Markdown）',
  },

  // 设置
  settings: {
    title: '设置',
    users: '用户',
    prompts: '快捷指令',
    agentProfiles: 'Agent 配置',
    featureRequests: '功能请求',
    llmUsage: 'LLM 用量',
    roles: '角色',
    userNotFound: '未找到用户',
    noUsersFound: '未找到用户',
    noFeatureRequests: '暂无功能请求',
    promptDeleted: '指令已删除',
    newPrompt: '新建指令',
    editPrompt: '编辑指令',
    egHermesAgent: '如：Hermes Agent',
    egTranslateToEnglish: '如：翻译为英文',
    addUser: '+ 添加用户',
    addNewUser: '添加新用户',
    editUser: '编辑用户',
    resetPassword: '重置密码',
    emailPlaceholder: '邮箱 *',
    passwordPlaceholder: '密码 *（至少8位）',
    nicknamePlaceholder: '昵称 *',
    dailyMsgLimit: '每日消息限额',
    monthlyTokenLimit: '每月Token限额',
    fillRequiredFields: '请填写所有必填项',
    passwordMinLength: '密码至少8位',
    passwordReset: '密码已重置',
    resetFailed: '重置失败',
    enterNewPassword: '输入新密码（至少8位）',
    toolAssignment: '工具分配',
    toolAssignTitle: '工具分配 — {name}',
    toolAssignHint: '为用户分配可使用的工具。全局工具始终可用，无需分配。',
    loadToolsFailed: '加载工具数据失败',
    toolsSaved: '工具分配已保存',
    disable: '禁用',
    enable: '启用',
    disableUserConfirm: '确定要{action}用户 {name} 吗？',
    totalUsers: '共 {count} 个用户',
    publicGlobal: 'Public (全局)',
    filterByRole: '全部角色',
    filterByStatus: '全部状态',
    sortByDefault: '默认',
    sortByTokenUsage: 'Token 用量',
    sortByTotalCalls: '总调用次数',
    sortByLastLogin: '最后登录',
    deleteUser: '删除',
    deleteUserConfirm: '确定要永久删除用户 {name} 吗？此操作不可撤销。',
    userDeleted: '用户已删除',
    deleteFailed: '删除失败',
    searchUsers: '搜索名称或邮箱...',
  },

  // 知识库（文档 + 导航 + 共享 + 版本）
  knowledge: {
    internalKb: '内部知识库',
    personalKb: '个人知识库',
    sharedWithMe: '共享给我',
    newDoc: '新建文档',
    renameSpace: '重命名分类',
    noDocs: '暂无文档',
    noPersonalDocs: '暂无个人文档',
    noSharedDocs: '暂无共享文档',
    searchDocs: '搜索文档...',
    searchSharedDocs: '搜索共享文档...',
    searchPersonalDocs: '搜索个人文档...',
    emptyInternalDesc: '创建你的第一篇内部知识文档。',
    emptyPersonalDesc: '创建你的第一篇个人知识文档。',
    emptySharedDesc: '别人共享给你的文档会显示在这里。',
    noArchivedDocs: '暂无归档文档',
    noArchivedDesc: '归档的文档会显示在这里。',
    docsCount: '{count} 篇文档',
    newDocBtn: '新建文档',
    statusActive: '活跃',
    editable: '可编辑',
    readOnly: '只读',
    history: '历史',
    versionHistory: '修改历史',
    share: '共享',
    updatedPrefix: '更新于',
    onThisPage: '本页目录',
    expandToc: '展开目录',
    collapseToc: '折叠目录',
    commonQuestions: '常见问题',
    relatedTopics: '相关主题',
    archiveConfirmTitle: '归档文档？',
    archiveConfirmTeam: '归档“{name}”？它将从已发布文档和 Agent 搜索中隐藏。',
    archiveConfirmSimple: '归档“{name}”？',
    editDocTitle: '编辑知识文档',
    newDocTitle: '新建知识文档',
    fieldTitle: '标题',
    titlePlaceholder: '文档标题',
    fieldVisibility: '可见性',
    visTeam: 'Team（内部）',
    visPrivate: 'Private（个人）',
    fieldStatus: '状态',
    advancedOptions: '高级选项（Space、标签、摘要 — 留空自动填充）',
    fieldSpace: 'Space',
    fieldTags: '标签（逗号分隔）',
    fieldSummary: '摘要',
    summaryPlaceholder: '留空自动截取正文前 160 字',
    titleRequired: '请填写标题',
    loadingVersions: '加载版本中…',
    noVersions: '暂无历史版本。',
    currentTag: '(当前)',
    updatedReason: '已更新',
    viewChange: '查看本次改动',
    restoreToVersion: '恢复到此版本',
    restoredToVersion: '已恢复到 v{version}',
    restoreFailed: '恢复失败',
    initialVersion: '初始版本',
    fieldContent: '正文',
    charsWord: '字符',
    diffInline: '行内',
    diffSplit: '并排',
    beforeLabel: '改前',
    afterLabel: '改后',
    noContentChange: '无正文变更（仅元数据）',
    shareDocTitle: '共享文档',
    sharingLabel: '共享：',
    sharedWith: '已共享给',
    revoke: '撤销',
    permission: '权限',
    roleReader: '只读 (reader)',
    roleEditor: '可编辑 (editor)',
    groups: '小组',
    noGroupsHint: '还没有小组 —— 创建一个就能一次共享给整组成员。',
    createGroupLink: '去创建小组',
    searchMembers: '搜索成员...',
    noMembers: '无成员',
    alreadyShared: '已共享',
    shareSuccess: '共享成功',
    shareFailed: '共享失败',
    revokeFailed: '撤销失败',
    loadShareFailed: '加载共享信息失败',
    loadFailed: '加载知识文档失败',
    docNotFound: '未找到知识文档',
    docCreated: '文档已创建',
    docUpdated: '文档已更新',
    docArchived: '文档已归档',
    docRestored: '文档已恢复',
    saveFailed: '保存文档失败',
    archiveFailed: '归档文档失败',
    restoreDocFailed: '恢复文档失败',
    createFailed: '创建文档失败',
    spaceRenamed: '分类已重命名 —— 移动了 {count} 篇文档',
    renameSpaceFailed: '重命名分类失败',
  },

  // 会话分组（每用户私有的会话文件夹 + 置顶）
  sessionGroups: {
    pinned: '置顶',
    pin: '置顶',
    unpin: '取消置顶',
    moveToGroup: '移动到分组',
    removeFromGroup: '移出分组',
    ungrouped: '未分组',
    newGroup: '新建分组',
    manageGroups: '管理分组',
    groupName: '分组名称',
    noGroups: '还没有分组。在下方创建你的第一个分组。',
    created: '分组已创建',
    updated: '分组已更新',
    deleted: '分组已删除',
    deleteTitle: '删除分组？',
    deleteDescription: '分组「{name}」将被移除。其中的会话不会被删除，会回到按日期排列的列表。',
    moved: '已移动',
    reorderFailed: '排序失败',
    dropHere: '拖到此处',
    showMore: '显示更多',
  },

  // 小组（知识共享小组）
  groups: {
    intro: '小组是知识库精细共享的目标。把个人知识文档共享给一个小组，组内成员即可访问。你创建的小组由你管理。',
    newGroupName: '新小组名称…',
    createGroup: '创建小组',
    noGroups: '还没有小组。',
    deleteGroup: '删除小组',
    deleteGroupTitle: '删除小组？',
    deleteGroupConfirm: '删除“{name}”？已共享给该小组的访问权限会一并失效。',
    memberOne: '名成员',
    memberOther: '名成员',
    noMembers: '暂无成员',
    remove: '移除',
    searchToAdd: '搜索成员添加…',
    noMatchingMembers: '无匹配成员',
    loadFailed: '加载小组失败',
    created: '小组已创建',
    createFailed: '创建失败',
    deleted: '小组已删除',
    deleteFailed: '删除失败',
    loadMembersFailed: '加载成员失败',
    addFailed: '添加失败',
    removeFailed: '移除失败',
  },

  // 技能中心（设置）
  skillCenter: {
    hint: '企业级技能库。成员通过聊天或 Greenhouse MCP（skill_query / skill_mutation）从各自的 AI 工具发布技能；在这里浏览、审计和管理。每个版本不可变更并附带变更说明。',
    empty: '还没有已发布的技能——让你的 Agent 通过 Greenhouse MCP 发布一个吧。',
    searchPlaceholder: '搜索技能…',
    colSkill: '技能',
    colDescription: '描述',
    colTags: '标签',
    colVersion: '最新版本',
    colDownloads: '下载次数',
    colStatus: '状态',
    colUpdated: '更新时间',
    statusActive: '启用中',
    statusArchived: '已归档',
    archive: '归档',
    unarchive: '取消归档',
    archived: '技能已归档',
    unarchived: '技能已恢复',
    actionFailed: '操作失败',
    owner: '所有者',
    createdAt: '创建时间',
    versionHistory: '版本历史',
    files: '个文件',
    viewFiles: '查看文件',
    hideFiles: '收起文件',
    downloadJson: '下载 JSON',
    by: '由',
    binaryFile: '二进制，base64',
  },

  // 软件更新（桌面端）
  updates: {
    title: '软件更新',
    desktopOnly: '软件更新仅在桌面应用中可用。',
    description: '检查并安装来自自托管更新源的新版本。界面热更新静默下载、重启生效。',
    checking: '检查中…',
    checkUpdates: '检查更新',
    appVersion: 'App 版本',
    uiHotUpdate: '界面热更新',
    builtinBaseline: '内置基线',
    updatedTo: '界面已更新到 v{version}，重启生效',
    upToDate: '已是最新版本',
    checkFailed: '检查更新失败：{error}',
  },

  // 自动化（定时任务）
  automations: {
    countLabel: '{count} 个自动化任务',
    create: '创建自动化',
    createTitle: '新建自动化',
    editTitle: '编辑任务',
    noneTitle: '暂无自动化任务',
    noneDesc: '创建一个定时任务，让 Agent 在设定时间自动运行。',
    taskName: '任务名称',
    taskNamePlaceholder: '每日工单分析',
    profile: 'Profile',
    profileHint: 'Profile 决定 Agent 可以使用哪些工具',
    taskPrompt: '任务提示词',
    taskPromptPlaceholder: '分析今天所有的服务工单，重点关注...',
    schedule: '排程',
    advancedSettings: '高级设置',
    maxSteps: '最大步数 (1-20)',
    timezone: '时区',
    createAndEnable: '创建并启用',
    colTask: '任务',
    colSchedule: '排程',
    colProfile: 'Profile',
    colStatus: '状态',
    colRuns: '运行次数',
    colLastRun: '上次运行',
    colActions: '操作',
    runNow: '立即运行',
    viewHistory: '查看历史',
    deleteTaskTitle: '删除任务',
    deleteTaskConfirm: '删除“{name}”？历史会话将保留。',
    runFailed: '运行失败',
    saveFailed: '保存失败',
    freqDaily: '每天',
    freqWeekdays: '工作日',
    freqWeekly: '每周',
    freqMonthly: '每月',
    freqCron: 'Cron 表达式',
    timeLabel: '时间',
    monthDayLabel: '日期',
    monthDayOption: '{day} 日',
    cronHint: '标准 5 段 cron 表达式：分 时 日 月 周',
    descDaily: '每天 {time} 运行',
    descWeekdays: '工作日（周一至周五）{time} 运行',
    descWeekly: '每周{days} {time} 运行',
    descMonthly: '每月 {day} 日 {time} 运行',
    weekdaySun: '日',
    weekdayMon: '一',
    weekdayTue: '二',
    weekdayWed: '三',
    weekdayThu: '四',
    weekdayFri: '五',
    weekdaySat: '六',
  },

  // LLM 网关管理
  llmGateway: {
    title: 'AI 网关',
    description: '内部用户通过服务端中继访问这些模型，无需个人密钥。在此配置上游厂商、发布模型并治理已签发的密钥。',
    upstreams: '上游池',
    addUpstream: '新增上游',
    editUpstream: '编辑上游',
    deleteUpstreamTitle: '删除上游？',
    deleteUpstreamDesc: '将移除“{name}”及映射到它的所有模型。',
    noUpstreams: '尚未配置上游。',
    addUpstreamFirst: '请先添加上游。',
    colName: '名称',
    colKind: '类型',
    colBaseUrl: 'Base URL',
    colKey: '密钥',
    colStatus: '状态',
    colActions: '操作',
    keySet: '已设置',
    keyNone: '无',
    enabled: '已启用',
    off: '关闭',
    fieldName: '名称',
    providerKind: '厂商类型',
    baseUrlLabel: 'Base URL（如上游需要请包含 /v1）',
    apiKeyLabel: 'API 密钥 {note}',
    apiKeyKeepNote: '（留空则保持不变）',
    enabledLabel: '启用',
    anthropicNote: 'Anthropic 上游已保存，但网关尚未提供服务（协议转换待实现）。',
    models: '模型',
    addModel: '新增模型',
    editModel: '编辑模型',
    deleteModelTitle: '删除模型？',
    deleteModelDesc: '将从目录中移除“{name}”。',
    noModels: '尚未发布模型。',
    colPublicId: 'Public ID',
    colDisplay: '显示名',
    colUpstream: '上游',
    colUpstreamModel: '上游模型',
    colFlags: '标记',
    flagOn: '开',
    flagOff: '关',
    flagDefault: '默认',
    flagPublic: '公开',
    publicIdLabel: 'Public ID（用户选择的标识）',
    displayName: '显示名',
    upstreamLabel: '上游',
    upstreamModelId: '上游模型 id',
    defaultSeamless: '默认（无缝）',
    publicSubset: '公开子集',
    sort: '排序',
    gatewayKeys: '网关密钥',
    colUser: '用户',
    colTodayTokens: '今日 Tokens',
    colDailyLimit: '每日上限',
    noKeys: '尚未签发网关密钥。',
    revoke: '吊销',
    enable: '启用',
  },

  // 收件箱
  inbox: {
    title: '收件箱',
    sharedWithMe: '分享给我的会话',
    unreadCount: '{count} 条未读',
    markAllRead: '全部已读',
    noShares: '暂无分享的会话',
    noSharesDesc: '当有人把会话分享给你时，会显示在这里',
    sharedYouSuffix: '分享给你',
  },

  // 用户菜单 / 账户
  userMenu: {
    usageStats: '用量统计',
    todayMessages: '今日消息',
    monthTokens: '本月 Tokens',
    loadingUsage: '加载用量中...',
    usageUnavailable: '用量不可用',
    noUsageData: '暂无用量数据',
    inbox: '收件箱',
    onlineUsers: '在线用户',
    onlineUsersTitle: '在线用户 ({count})',
    noOnlineUsers: '暂无在线用户',
    availableProfiles: '可用 Profiles',
  },

  // 媒体预览弹窗
  media: {
    viewAttachment: '查看附件 {current} / {total}',
    prev: '上一个',
    next: '下一个',
    download: '下载',
    imageLoadFailed: '图片加载失败',
    tryDownload: '尝试下载',
    videoNotSupported: '您的浏览器不支持视频播放',
    unknownFile: '未知文件',
    downloadFile: '下载文件',
  },

  // MCP 选择器
  mcp: {
    selectedFailure: '已选中的某个 MCP 服务不可达 — 展开查看详情',
    selectServers: '选择本次聊天要使用的 MCP 服务',
    injectHint: '选中的 MCP 会在新建聊天时注入到 Agent。',
    recheck: '重新检测连通性',
    loading: '加载中…',
    connectFailed: '连接失败',
    probing: '检测中…',
    reachable: '可连接',
  },

  // 桌面宠物状态标签
  pet: {
    localRuntimeUnavailable: '本地运行时不可用',
    startFailed: '启动失败',
    cloudTaskRunning: '云端任务进行中…',
    done: '完成',
    errored: '出错了',
  },

  // 本地文件卡片
  localFiles: {
    openFileFailed: '打开文件失败',
    revealFailed: '打开所在位置失败',
    doubleClickToOpen: '双击打开文件',
    opening: '打开中',
    open: '打开',
    revealLocation: '打开所在位置',
  },

  // 管理面板 FAQ 分类
  faq: {
    errorCode: '设备错误码',
    productIssue: '产品问题',
    commonQuestion: '常见问题',
  },

  toolSpawn: {
    subSession: '子会话',
    running: '运行中…',
    done: '已完成',
    open: '打开',
  },

  // 生成文件下载卡片（export_table 等）
  exportFile: {
    download: '下载',
    expired: '已过期',
    expiredHint: '下载链接已过期，重新提问即可再次生成文件。',
  },

  // 聊天消息操作
  messageActions: {
    translateToChinese: '翻译为中文',
    zh: '中',
  },

  // 健康指示器
  health: {
    connected: '已连接 · {model} · {pages} 个文档',
    unreachable: 'API 不可达',
    checking: '检查中...',
  },

  // 工具调用
  tools: {
    adminNote: '管理员备注...',
    adminNotesAbout: '管理员对此次回复的备注...',
  },

  // PDF 导出
  pdf: {
    exportAsPdf: '导出 PDF',
  },

  // 斜杠命令 / 提及
  slashCommands: {
    createPromptIn: ' 中创建快捷指令',
  },

  // Profile 编辑器
  profileEditor: {
    descriptionPlaceholder: '简要描述该配置的用途',
    inheritsModel: '继承基础配置的模型设置',
  },
} as const;

export default zh;
